import { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductReviews = ({ productId }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [comment, setComment] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const [reviewsRes, ratingRes] = await Promise.all([
        axios.get(`${API}/products/${productId}/reviews`),
        axios.get(`${API}/products/${productId}/rating`)
      ]);
      setReviews(reviewsRes.data);
      setAvgRating(ratingRes.data.avg_rating);
      setReviewCount(ratingRes.data.review_count);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Selecione uma avaliação');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/products/${productId}/reviews`, {
        product_id: productId,
        rating,
        comment: comment || null
      });
      toast.success('Avaliação enviada!');
      setRating(0);
      setComment('');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const StarRating = ({ value, interactive = false, size = 'w-5 h-5' }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
            onClick={interactive ? () => setRating(star) : undefined}
            onMouseEnter={interactive ? () => setHoveredStar(star) : undefined}
            onMouseLeave={interactive ? () => setHoveredStar(0) : undefined}
            disabled={!interactive}
          >
            <Star
              className={`${size} ${
                star <= (interactive ? (hoveredStar || rating) : value)
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-600'
              } transition-colors`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="mt-16" data-testid="product-reviews">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-2xl">Avaliações</h2>
        {reviewCount > 0 && (
          <div className="flex items-center gap-3">
            <StarRating value={avgRating} />
            <span className="text-lg font-bold">{avgRating.toFixed(1)}</span>
            <span className="text-muted-foreground">({reviewCount} avaliações)</span>
          </div>
        )}
      </div>

      {/* Write Review Form */}
      {user ? (
        <div className="bg-card border border-white/10 p-6 mb-8">
          <h3 className="font-bold mb-4">Deixe sua avaliação</h3>
          <form onSubmit={handleSubmitReview}>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Sua nota:</p>
              <StarRating value={rating} interactive={true} size="w-8 h-8" />
            </div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Conte sua experiência com o produto (opcional)"
              className="input-luxury min-h-[100px] mb-4"
            />
            <Button
              type="submit"
              className="btn-primary"
              disabled={submitting || rating === 0}
            >
              {submitting ? 'Enviando...' : 'Enviar Avaliação'}
            </Button>
          </form>
        </div>
      ) : (
        <div className="bg-card border border-white/10 p-6 mb-8 text-center">
          <p className="text-muted-foreground">
            <a href="/auth" className="text-primary hover:underline">Faça login</a> para deixar uma avaliação
          </p>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nenhuma avaliação ainda</p>
          <p className="text-sm mt-2">Seja o primeiro a avaliar este produto!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="border-b border-white/10 pb-6" data-testid={`review-${review.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{review.user_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(review.created_at)}</p>
                  </div>
                </div>
                <StarRating value={review.rating} size="w-4 h-4" />
              </div>
              {review.comment && (
                <p className="text-muted-foreground ml-13">{review.comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
